import { Plus, Trash2, ChevronUp, ChevronDown } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MLFields, toML, locRu } from "./_ui";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPES = [
  { value: "text", label: "Текст → строка в акте" },
  { value: "vin", label: "VIN (номер шасси)" },
  { value: "inspection_date", label: "Дата осмотра" },
  { value: "comment", label: "Комментарий (состояние авто)" },
];

export default function SpecFieldsEditor({ fields, onChange }) {
  const list = fields || [];
  const upd = (i, k, v) => onChange(list.map((f, idx) => (idx === i ? { ...f, [k]: v } : f)));
  const add = () =>
    onChange([...list, { key: `field_${Date.now()}`, label: toML(""), type: "text", order: list.length + 1, active: true, check_language: true, hint: "" }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i).map((f, idx) => ({ ...f, order: idx + 1 })));
  const move = (i, d) => {
    const j = i + d; if (j < 0 || j >= list.length) return;
    const next = [...list]; [next[i], next[j]] = [next[j], next[i]];
    onChange(next.map((f, idx) => ({ ...f, order: idx + 1 })));
  };

  return (
    <div className="border border-neutral-200 rounded-lg bg-white p-5 space-y-4">
      <div>
        <h3 className="font-semibold text-sm">Поля тех.характеристик (бот)</h3>
        <p className="text-xs text-neutral-500 mt-1">
          Бот показывает поле по-русски и печатает в акт на языке акта. Ярлык хранится на 4 языках.
          «Проверять язык» — блокировать кириллицу для актов DE/EN/PL (для текстовых полей).
        </p>
      </div>

      <div className="space-y-3">
        {list.map((f, i) => (
          <div key={f.key || i} className="border border-neutral-200 rounded-md p-3 space-y-3" data-testid={`specfield-${i}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-medium">{locRu(f.label) || "(без названия)"}</span>
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <button type="button" onClick={() => move(i, -1)} className="text-neutral-400 hover:text-black"><ChevronUp className="w-4 h-4" /></button>
                  <button type="button" onClick={() => move(i, 1)} className="text-neutral-400 hover:text-black"><ChevronDown className="w-4 h-4" /></button>
                </div>
                <button type="button" onClick={() => remove(i)} className="text-red-600 p-1" data-testid={`specfield-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <MLFields value={toML(f.label)} onChange={(v) => upd(i, "label", v)} testid={`specfield-label-${i}`} />
            <div className="space-y-1">
              <Label className="text-xs text-neutral-500">Подсказка (пример для менеджера, на русском)</Label>
              <Input value={f.hint || ""} onChange={(e) => upd(i, "hint", e.target.value)} placeholder="Например: Полное название модели. Mercedes-AMG G 63 4MATIC+" data-testid={`specfield-hint-${i}`} />
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Select value={f.type || "text"} onValueChange={(v) => upd(i, "type", v)}>
                <SelectTrigger className="w-[210px]" data-testid={`specfield-type-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="flex items-center gap-1.5">
                <Switch checked={f.active !== false} onCheckedChange={(v) => upd(i, "active", v)} data-testid={`specfield-active-${i}`} />
                <span className="text-xs text-neutral-500">вкл</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Switch checked={!!f.check_language} onCheckedChange={(v) => upd(i, "check_language", v)} data-testid={`specfield-checklang-${i}`} />
                <span className="text-xs text-neutral-500">проверять язык</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button type="button" variant="outline" size="sm" onClick={add} data-testid="specfield-add">
        <Plus className="w-3.5 h-3.5 mr-1" /> Добавить поле
      </Button>
    </div>
  );
}
